/**
 * useElectionMap Composable
 *
 * Provides D3 geo projection and path generation for US state map rendering.
 * Uses us-atlas TopoJSON data for state boundaries.
 *
 * @see https://billmill.org/making_a_us_map.html
 */
import { shallowRef, computed, type Ref, type ComputedRef } from 'vue';
import { geoPath, geoAlbersUsa, type GeoPath, type GeoPermissibleObjects } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import type { Feature, Geometry } from 'geojson';
import {
  FIPS_TO_STATE,
  PARTY_COLORS,
  type USStateCode,
  type ElectionContract,
  type PartyCode,
} from '../types/election.types';

// GeoJSON Feature with state ID property
export interface StateFeature extends Feature<Geometry> {
  id: string;
  properties: Record<string, unknown>;
}

// Return type for the composable
export interface UseElectionMapReturn {
  /** TopoJSON data (loaded async) */
  topoData: Ref<Topology | null>;
  /** Computed array of state GeoJSON features */
  states: ComputedRef<StateFeature[]>;
  /** Loading state */
  isLoading: Ref<boolean>;
  /** Error state */
  error: Ref<Error | null>;
  /** Load the TopoJSON map data */
  loadMap: () => Promise<void>;
  /** Generate SVG path string for a state feature */
  getPath: (state: StateFeature) => string;
  /** Convert FIPS ID to state code */
  getStateCode: (fipsId: string) => USStateCode | undefined;
  /** Get fill color based on election data */
  getStateColor: (
    stateCode: USStateCode,
    elections: Record<USStateCode, ElectionContract>,
    selectedState?: USStateCode | null,
  ) => string;
  /** Get party color by code */
  getPartyColor: (party: PartyCode, intensity?: 'dark' | 'mid' | 'light') => string;
  /** Get centroid [x, y] for a state feature */
  getCentroid: (state: StateFeature) => [number, number];
  /** Map dimensions for SVG viewBox */
  dimensions: { width: number; height: number };
}

/**
 * Composable for US election map rendering with D3.js
 *
 * @example
 * ```vue
 * <script setup>
 * const { states, loadMap, getPath, getStateColor } = useElectionMap()
 * onMounted(() => loadMap())
 * </script>
 * <template>
 *   <svg viewBox="0 0 975 610">
 *     <path v-for="state in states" :d="getPath(state)" :fill="getStateColor(state.id, elections)" />
 *   </svg>
 * </template>
 * ```
 */
export function useElectionMap(): UseElectionMapReturn {
  // Use shallowRef for TopoJSON data (immutable after load, prevents deep reactivity)
  const topoData = shallowRef<Topology | null>(null);
  const isLoading = shallowRef(false);
  const error = shallowRef<Error | null>(null);

  // Map dimensions matching standard US Albers projection
  const dimensions = { width: 975, height: 610 };

  // Cache projection and path generator outside reactive system for performance
  const projection = geoAlbersUsa()
    .scale(1300)
    .translate([dimensions.width / 2, dimensions.height / 2]);

  const pathGenerator: GeoPath<unknown, GeoPermissibleObjects> = geoPath().projection(projection);

  /**
   * Computed array of state features from TopoJSON
   * Converts TopoJSON topology to GeoJSON features
   */
  const states = computed<StateFeature[]>(() => {
    if (!topoData.value) return [];

    const topology = topoData.value;
    const statesObject = topology.objects.states as GeometryCollection;

    if (!statesObject) return [];

    const featureCollection = feature(topology, statesObject);
    return featureCollection.features as StateFeature[];
  });

  /**
   * Load US Atlas TopoJSON data
   * Uses dynamic import for code splitting
   */
  async function loadMap(): Promise<void> {
    if (topoData.value || isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      // Dynamic import for code splitting - loads ~200KB TopoJSON
      const usAtlas = await import('us-atlas/states-10m.json');
      topoData.value = usAtlas.default as unknown as Topology;
    } catch (e) {
      error.value = e instanceof Error ? e : new Error('Failed to load map data');
      console.error('Failed to load US Atlas data:', e);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Generate SVG path string for a state feature
   */
  function getPath(state: StateFeature): string {
    return pathGenerator(state) || '';
  }

  // Manual centroid offsets for irregularly shaped states [dx, dy]
  const CENTROID_OFFSETS: Record<string, [number, number]> = {
    FL: [20, 10], // Pull left and up into peninsula body
    MI: [20, 30], // Push down into lower peninsula
    LA: [-15, -10], // Pull up from coast into state body
    HI: [17, 12], // Center on main islands
    ID: [0, 12], // Push down into wider southern part
    CA: [0, 20], // Push down into wider central area
  };

  /**
   * Get centroid [x, y] coordinates for a state feature
   * Applies manual offsets for irregularly shaped states
   */
  function getCentroid(state: StateFeature): [number, number] {
    const c = pathGenerator.centroid(state);
    const stateCode = FIPS_TO_STATE[state.id];
    const offset = stateCode ? CENTROID_OFFSETS[stateCode] : undefined;
    if (offset) {
      return [c[0] + offset[0], c[1] + offset[1]];
    }
    return [c[0], c[1]];
  }

  /**
   * Convert FIPS ID to state code
   */
  function getStateCode(fipsId: string): USStateCode | undefined {
    return FIPS_TO_STATE[fipsId];
  }

  /**
   * Get fill color for a state based on election data
   * Colors intensity based on leading party's price (confidence level)
   */
  function getStateColor(
    stateCode: USStateCode,
    elections: Record<USStateCode, ElectionContract>,
    selectedState?: USStateCode | null,
  ): string {
    const election = elections[stateCode];

    // Highlight selected state
    if (selectedState === stateCode) {
      return election?.favorite === 'GOP' ? PARTY_COLORS.GOP_DARK : PARTY_COLORS.DEM_DARK;
    }

    if (!election) {
      return PARTY_COLORS.NEUTRAL;
    }

    const { favorite, price } = election;

    // Calculate intensity based on price (confidence level)
    // Higher price = more confident = darker color
    if (favorite === 'GOP') {
      if (price >= 80) return PARTY_COLORS.GOP_DARK;
      if (price >= 60) return PARTY_COLORS.GOP_MID;
      return PARTY_COLORS.GOP_LIGHT;
    } else {
      if (price >= 80) return PARTY_COLORS.DEM_DARK;
      if (price >= 60) return PARTY_COLORS.DEM_MID;
      return PARTY_COLORS.DEM_LIGHT;
    }
  }

  /**
   * Get party color by code and intensity
   */
  function getPartyColor(party: PartyCode, intensity: 'dark' | 'mid' | 'light' = 'dark'): string {
    if (party === 'GOP') {
      return intensity === 'dark'
        ? PARTY_COLORS.GOP_DARK
        : intensity === 'mid'
          ? PARTY_COLORS.GOP_MID
          : PARTY_COLORS.GOP_LIGHT;
    } else if (party === 'DEM') {
      return intensity === 'dark'
        ? PARTY_COLORS.DEM_DARK
        : intensity === 'mid'
          ? PARTY_COLORS.DEM_MID
          : PARTY_COLORS.DEM_LIGHT;
    } else if (party === 'GRN') {
      return intensity === 'dark'
        ? PARTY_COLORS.GRN_DARK
        : intensity === 'mid'
          ? PARTY_COLORS.GRN_MID
          : PARTY_COLORS.GRN_LIGHT;
    }
    return PARTY_COLORS.NEUTRAL;
  }

  return {
    topoData,
    states,
    isLoading,
    error,
    loadMap,
    getPath,
    getCentroid,
    getStateCode,
    getStateColor,
    getPartyColor,
    dimensions,
  };
}
